import * as React from "react";
import { User, UserManager, WebStorageStateStore } from "oidc-client-ts";
import { getOidcConfig } from "../utils/oidcConfig";

interface UserClaim {
    issuer: string;
    originalIssuer: string;
    type: string;
    value: string;
    valueType: string;
}

interface UserInfo {
    firstName: string;
    lastName: string;
    fullName: string;
    isDisabled: boolean;
    type: number;
    thirdPartyLogin: boolean;
    dateCreated: string;
    dateUpdated: string;
    startDate: string;
    endDate: string;
    lastLoginDate: string;
    claims: UserClaim[];
    groups: any[];
    id: string;
    userName: string;
    email: string;
    emailConfirmed: boolean;
    phoneNumber: string;
    phoneNumberConfirmed: boolean;
    twoFactorEnabled: boolean;
    lockoutEnd: string;
    lockoutEnabled: boolean;
    permissions: any[];
}

interface AuthContextState {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    accessToken: string | null;
    signIn: (returnTo?: string) => Promise<void>;
    signOut: () => Promise<void>;
    handleSignInCallback: () => Promise<string | undefined>;
    clearError: () => void;
    userData: UserInfo;
}

const AuthContext = React.createContext<AuthContextState | undefined>(undefined);

const createUserManager = () => {
    const config = getOidcConfig();
    return new UserManager({
        authority: config.authority,
        client_id: config.clientId,
        redirect_uri: config.redirectUri,
        post_logout_redirect_uri: config.postLogoutRedirectUri,
        silent_redirect_uri: config.silentRedirectUri,
        scope: config.scope,
        response_type: config.responseType,
        automaticSilentRenew: config.automaticSilentRenew,
        userStore: new WebStorageStateStore({ store: window.localStorage }),
    });
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const managerRef = React.useRef<UserManager>();
    if (!managerRef.current) {
        managerRef.current = createUserManager();
    }

    const manager = managerRef.current;

    const [user, setUser] = React.useState<User | null>(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [userData, setUserData] = React.useState<any>(null);
    const config = getOidcConfig();

    const loadUser = React.useCallback(async () => {
        try {
            const current = await manager.getUser();
            setUser(current);
        } catch (err) {
            console.error("Failed to load user from storage", err);
            setError(err instanceof Error ? err.message : "Failed to load authentication state");
        } finally {
            setIsLoading(false);
        }
    }, [manager]);

    React.useEffect(() => {
        let disposed = false;
        loadUser();

        const onUserLoaded = (loaded: User) => {
            if (!disposed) {
                setUser(loaded);
            }
        };

        const onUserUnloaded = () => {
            if (!disposed) {
                setUser(null);
            }
        };

        const onAccessTokenExpired = async () => {
            try {
                const refreshed = await manager.signinSilent();
                if (!disposed) {
                    setUser(refreshed);
                }
            } catch (err) {
                console.warn("Silent token renewal failed", err);
                if (!disposed) {
                    setError(err instanceof Error ? err.message : "Authentication session expired");
                    setUser(null);
                }
            }
        };

        manager.events.addUserLoaded(onUserLoaded);
        manager.events.addUserUnloaded(onUserUnloaded);
        manager.events.addAccessTokenExpired(onAccessTokenExpired);

        return () => {
            disposed = true;
            manager.events.removeUserLoaded(onUserLoaded);
            manager.events.removeUserUnloaded(onUserUnloaded);
            manager.events.removeAccessTokenExpired(onAccessTokenExpired);
        };
    }, [loadUser, manager]);

    const signIn = React.useCallback(
        async (returnTo?: string) => {
            setIsLoading(true);
            setError(null);
            const nextState = {
                returnTo: returnTo ?? `${window.location.pathname}${window.location.search}`,
            };
            await manager.signinRedirect({ state: nextState });
        },
        [manager]
    );

    const signOut = React.useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            await manager.signoutRedirect();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to sign out");
            setIsLoading(false);
        }
    }, [manager]);

    const handleSignInCallback = React.useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await manager.signinRedirectCallback();
            setUser(response);
            const state = response?.state as { returnTo?: string } | undefined;
            return state?.returnTo;
        } catch (err) {
            console.error("Failed to complete OIDC sign-in", err);
            setError(err instanceof Error ? err.message : "Unable to complete sign-in");
            return undefined;
        } finally {
            setIsLoading(false);
        }
    }, [manager]);

    const fetchUserData = async (token: string) => {
        if (token) {
            const data = await fetch(config.authority + "/account/currentloggedinuser", { headers: { Authorization: `IdentityBearer ${token}` } })
                .then((res) => res.json())
                .catch(() => {});
            setUserData(data);
        }
    };

    React.useEffect(() => {
        fetchUserData(user?.access_token ?? "");
    }, [user]);

    const value = React.useMemo<AuthContextState>(
        () => ({
            user,
            isAuthenticated: !!user && !user.expired,
            isLoading,
            error,
            accessToken: user?.access_token ?? null,
            signIn,
            signOut,
            handleSignInCallback,
            clearError: () => setError(null),
            userData,
        }),
        [user, isLoading, error, signIn, signOut, handleSignInCallback, userData]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextState => {
    const context = React.useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }

    return context;
};
